import React, { ReactElement } from 'react';
import { Accordion, Icon } from 'semantic-ui-react';
import { withProperties } from '$/utils/type';
import { SerializedStyles, css } from '@emotion/react';
import { AccordionTitleProps } from 'semantic-ui-react/dist/commonjs/modules/Accordion/AccordionTitle';
import PermissionedLink from '$/components/Router/PermissionedLink';
import { StyleProps } from '.';
import tw, { theme } from 'twin.macro';

interface Props {
  activeSection: number;
  children: ReactElement[];
  onActiveSectionChange?: (index: number) => void;
  className?: string;
}

interface MyAccordionItemProps extends Omit<AccordionTitleProps, 'onClick'> {
  id: string;
  index?: number;
  title?: string;
  active?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>, data: AccordionTitleProps) => void;
  className?: string;
  icon: React.FC<StyleProps>;
  to: string;
  skipDirtyForm?: boolean;
  ss?: SerializedStyles;
  showFn?: () => boolean;
}

const AccordionItem: React.FunctionComponent<MyAccordionItemProps> = ({
  id,
  index,
  children,
  title,
  active,
  className,
  to,
  icon,
  onClick,
  skipDirtyForm,
  ss,
  showFn: useShowFn,
}) => {
  const ItemIcon = icon;

  if (!useShowFn) {
    useShowFn = (): boolean => true;
  }
  const show = useShowFn();
  if (!show) {
    return null;
  }

  return (
    <PermissionedLink
      id={id}
      className='no-underline'
      to={to}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>): boolean => {
        if (active === index) {
          e.preventDefault();
          return true;
        }
        onClick && onClick(e, { index: index });
        return true;
      }}
      skipDirtyForm={skipDirtyForm}
    >
      <div
        className={`${className} flex justify-start border-l-2 rounded-r-lg`}
        css={[
          css`
            ${tw`font-body`}
            width: calc(100% - 1.5rem);
            padding: 1rem 0 1rem 1.5rem;
            background: ${active === index ? '#f1f5f5' : 'transparent'};
            color: ${active === index ? theme`colors.color.brand` : theme`colors.color.menu`};
            border-color: ${active === index ? theme`colors.color.brand` : '#ffffff'};
            &:hover {
              background: #f1f5f5;
              border-color: ${theme`colors.color.brand`};
            }
          `,
          ss,
        ]}
      >
        <div className='inner-item'>
          <Accordion.Title
            className='ui flex flex-1 justify-between items-center font-normal normal-case'
            active={active === index}
            index={index}
          >
            <div className='header-inner flex items-center min-w-3/4'>
              <ItemIcon className='w-1_25e h-1_25e mr-1_5e' />
              <div className='flex title-div'>{title}</div>
            </div>
            {Boolean(React.Children.count(children)) && <Icon name='dropdown' />}
          </Accordion.Title>
          {Boolean(React.Children.count(children)) && (
            <Accordion.Content
              className='ui'
              css={css`
                padding-top: 1rem !important;
              `}
              active={active === index}
            >
              {children}
            </Accordion.Content>
          )}
        </div>
      </div>
    </PermissionedLink>
  );
};

const CustomLinkAccordion: React.FunctionComponent<Props> = ({
  children,
  activeSection: active,
  onActiveSectionChange,
  className,
}) => {
  const onClick = (
    event: React.MouseEvent<HTMLDivElement>,
    titleProps?: AccordionTitleProps
  ): void => {
    event.preventDefault();

    if (!titleProps) {
      return;
    }

    const { index } = titleProps;
    if (active === index) {
      return;
    }
    const newIndex = index;
    onActiveSectionChange && onActiveSectionChange(Number(newIndex));
  };

  return (
    <div className={className}>
      <Accordion>
        {children.map((child: ReactElement, index: number) =>
          React.cloneElement(child, {
            active,
            index,
            key: index,
            onClick,
          })
        )}
      </Accordion>
    </div>
  );
};

export default withProperties(CustomLinkAccordion, {
  Item: AccordionItem,
});
